import { useEffect, useMemo, useState } from "react";
import { useFetcher } from "react-router";
import type { PharmacyPickerItem } from "~/features/products/pharmacy-picker/pharmacy-picker.types";

const AVAILABILITY_ENDPOINT = "/api/express-courier/availability";

type AvailabilityResponse =
	| { results: Array<{ vendorId: number; available: boolean }> }
	| { error: string };

type UsePdpExpressCourierFilterParams = {
	/** The chip exists on courier only, and only with a resolved address. */
	deliveryMethodKind?: string | null;
	postalCode?: string | null;
	pharmacies: PharmacyPickerItem[];
};

type UsePdpExpressCourierFilterResult = {
	filterProps: {
		expressSelected?: boolean;
		onExpressChange?: (selected: boolean) => void;
	};
	filteredPharmacies: PharmacyPickerItem[];
	/** The chip is on and the Lieferando zone check could not be completed. */
	checkFailed: boolean;
};

const hasResults = (
	response: AvailabilityResponse | undefined,
): response is { results: Array<{ vendorId: number; available: boolean }> } =>
	response != null && "results" in response;

const vendorIdOf = (item: PharmacyPickerItem): number | null => {
	const id = Number(item.id);
	return Number.isInteger(id) && id > 0 ? id : null;
};

const lieferandoKept = (
	item: PharmacyPickerItem,
	settled: boolean,
	checkFailed: boolean,
	availableVendorIds: ReadonlySet<number> | null,
): boolean => {
	if (!item.requiresExpressZoneCheck) {
		return false;
	}
	const vendorId = vendorIdOf(item);
	if (vendorId == null || checkFailed) {
		return false;
	}
	if (!settled || availableVendorIds == null) {
		return true;
	}
	return availableVendorIds.has(vendorId);
};

/**
 * Express-courier filter for the PDP pharmacy picker.
 *
 * Uber's enabled flag already includes the stored delivery point and the open
 * window, so those pharmacies stay without a further check. Lieferando's
 * enabled flag does not include the address zone, so the chip asks for that
 * verdict and keeps a pharmacy only when the check says it can deliver. The
 * row stays until the verdict arrives. A failed check drops it and is reported
 * on its own. Postal-code distance is a different point and is not the zone.
 */
export function usePdpExpressCourierFilter({
	deliveryMethodKind,
	postalCode,
	pharmacies,
}: UsePdpExpressCourierFilterParams): UsePdpExpressCourierFilterResult {
	const [selected, setSelected] = useState(false);
	const fetcher = useFetcher<AvailabilityResponse>();
	const resolvedPostalCode = postalCode ?? "";
	const showFilter =
		deliveryMethodKind === "courier" && resolvedPostalCode !== "";
	const active = showFilter && selected;
	const zoneCheckVendorIds = useMemo(
		() =>
			pharmacies
				.filter((item) => item.requiresExpressZoneCheck)
				.map(vendorIdOf)
				.filter((id): id is number => id != null),
		[pharmacies],
	);
	const requestKey = active
		? `${resolvedPostalCode}|${zoneCheckVendorIds.join(",")}`
		: "";
	// biome-ignore lint/correctness/useExhaustiveDependencies: fetcher is a new object each render; requestKey identifies the request
	useEffect(() => {
		if (requestKey === "" || zoneCheckVendorIds.length === 0) {
			return;
		}
		fetcher.submit(
			{ vendorIds: zoneCheckVendorIds, postalCode: resolvedPostalCode },
			{
				method: "POST",
				action: AVAILABILITY_ENDPOINT,
				encType: "application/json",
			},
		);
	}, [requestKey]);
	const availableVendorIds = useMemo(() => {
		if (!hasResults(fetcher.data)) {
			return null;
		}
		return new Set(
			fetcher.data.results
				.filter((result) => result.available)
				.map((result) => result.vendorId),
		);
	}, [fetcher.data]);
	const settled = fetcher.state === "idle" && fetcher.data != null;
	const checkFailed =
		active &&
		zoneCheckVendorIds.length > 0 &&
		settled &&
		!hasResults(fetcher.data);
	const filteredPharmacies = useMemo(() => {
		if (!active) {
			return pharmacies;
		}
		return pharmacies.filter(
			(item) =>
				item.offersUberExpress === true ||
				lieferandoKept(item, settled, checkFailed, availableVendorIds),
		);
	}, [active, pharmacies, settled, checkFailed, availableVendorIds]);
	return {
		filterProps: showFilter
			? { expressSelected: selected, onExpressChange: setSelected }
			: {},
		filteredPharmacies,
		checkFailed,
	};
}
